import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { apiCallOperations } from 'Redux/ducks/apiCall';
import ErrorPanel from 'ViatoUi/Extranet/DataDisplay/ErrorPanel';
import Alert from 'ViatoUi/Extranet/Surfaces/Alert';
import Modal from 'ViatoUi/Extranet/Surfaces/Modal';
import Icon from 'ViatoUi/Shared/DataDisplay/Icon';
import { snackbar } from 'ViatoUi/Shared/Feedback/Snackbar';
import { FlexBox } from 'ViatoUi/Shared/Layouts/Flex';
import Loader from 'ViatoUi/Shared/Loader';
import Paragraph from 'ViatoUi/Shared/Typo/Paragraph';

const UserActionFeedback = ({
	pending,
	error,
	errorCode,
	errorMessage,
	onDone,
	type,
	userMessage,
	showLoader,
	loaderIconOnly,
	isSilent,
	onError,
	onSuccess,
	showContractLoader
}) => {
	const { t } = useTranslation('viatoUi');
	const dispatch = useDispatch();

	const [modalContent, setModalContent] = useState({ visible: false, content: <></>, title: '' });
	const [dataHasBeenSubmitted, setDataHasBeenSubmitted] = useState(false);
	const [pendingStateChanged, setPendingStateChanged] = useState(false);

	useEffect(() => {
		if (pending) {
			setPendingStateChanged(true);
		}
		if (!pending && pendingStateChanged) {
			setDataHasBeenSubmitted(true);
		}
	}, [pending]);

	const snackbarPendingId = useRef(null);

	useEffect(() => {
		setTimeout(() => {
			if (type === 'toast') {
				if (pending) {
					dispatch(apiCallOperations.resetAllErrors());

					snackbarPendingId.current = snackbar.bright(
						<FlexBox alignItems='center'>
							<Icon color='dark' icon='fa-spinner-third fa-spin' iconWeight='fas' size='s' spaceAfter />{' '}
							<Paragraph font='normal' noBreak>
								{t('app.feedback.data.processing')}
							</Paragraph>
						</FlexBox>,
						{
							autoClose: false,
							closeButton: false,
							closeOnClick: false
						}
					);
				}
			}
		}, 20);
	}, [pending]);

	useEffect(() => {
		setTimeout(() => {
			if (type === 'modal' && dataHasBeenSubmitted && !pending) {
				if (error) {
					setModalContent({
						visible: true,
						content: (
							<ErrorPanel
								errorCode={errorCode}
								errorMessage={errorMessage}
								userMessage={userMessage || t('app.feedback.save.error')}
							/>
						),
						title: t('app.feedback.save.error')
					});
				}
				if (!error) {
					setModalContent({
						visible: !isSilent,
						content: <Alert type='success'>{t('app.feedback.save.success')}</Alert>,
						title: t('app.feedback.save.success')
					});

					if (isSilent) {
						onDone();
						setDataHasBeenSubmitted(false);
						setPendingStateChanged(false);
						onSuccess();
					}
				}
			}

			if (type === 'toast' && !pending) {
				snackbar.dismiss(snackbarPendingId.current);
				if (dataHasBeenSubmitted) {
					if (error) {
						snackbar.error(
							<>
								<FlexBox alignItems='center'>
									<Icon color='bright' icon='fa-exclamation-triangle' iconWeight='fas' size='s' spaceAfter />{' '}
									<Paragraph font='normal'> {userMessage || t('app.feedback.save.error')}</Paragraph>
								</FlexBox>
								<div style={{ maxWidth: '600px', overflowX: 'auto' }}>
									<Paragraph font='tiny'>
										{errorCode}:{errorMessage}
									</Paragraph>
								</div>
							</>
						);
						onError();
					}
					if (!error) {
						snackbar.success(
							<FlexBox alignItems='center'>
								<Icon color='bright' icon='fa-check-circle' iconWeight='fas' size='s' spaceAfter />{' '}
								<Paragraph font='normal' noBreak>
									{t('app.feedback.save.success')}
								</Paragraph>
							</FlexBox>
						);
						onSuccess();
					}

					onDone();
					setDataHasBeenSubmitted(false);
					setPendingStateChanged(false);
				}
			}
		}, 20);
	}, [pending, error, dataHasBeenSubmitted]);

	return type === 'modal' ? (
		<>
			{pending && showLoader && <Loader iconOnly={loaderIconOnly} useContractText={showContractLoader} />}
			<Modal
				title={modalContent.title}
				visible={modalContent.visible}
				width='425px'
				onClose={() => {
					setModalContent({ visible: false, content: <></>, title: '' });
					onDone();
					setDataHasBeenSubmitted(false);
					setPendingStateChanged(false);
					if (error) {
						onError();
						dispatch(apiCallOperations.resetAllErrors());
					} else {
						onSuccess();
					}
				}}
			>
				{modalContent.content}
			</Modal>
		</>
	) : null;
};

UserActionFeedback.propTypes = {
	isSilent: PropTypes.bool,
	showContractLoader: PropTypes.bool,
	showLoader: PropTypes.bool,
	pending: PropTypes.bool.isRequired,
	error: PropTypes.bool.isRequired,
	errorCode: PropTypes.number,
	errorMessage: PropTypes.string,
	userMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
	onDone: PropTypes.func,
	onError: PropTypes.func,
	type: PropTypes.oneOf(['toast', 'modal']),
	loaderIconOnly: PropTypes.bool,
	onSuccess: PropTypes.func
};

UserActionFeedback.defaultProps = {
	isSilent: false,
	showContractLoader: false,
	showLoader: true,
	errorCode: 0,
	errorMessage: '',
	onDone: () => {},
	onError: () => {},
	onSuccess: () => {},
	type: 'toast',
	userMessage: null,
	loaderIconOnly: false
};

export default UserActionFeedback;
